import { Component, EventEmitter, inject, Input, Output, signal } from '@angular/core';
import { environment } from '../../../environments/environment';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
// Service
import { UploadImageService } from './upload-image.service';
import { LoggingService } from '../../core/utils/logging.service';
// PrimeNG
import { SkeletonModule } from 'primeng/skeleton';
import {
  DialogAlertComponent,
  IDialogData,
  PATH_ICON_ALERT_DIALOG,
} from '../../ui/dialog-alert/dialog-alert.component';
import { NotificationBarService } from '../notification-bar';

@Component({
  selector: 'app-upload-image',
  imports: [TranslocoModule, SkeletonModule, DialogAlertComponent],
  templateUrl: './upload-image.component.html',
  styleUrl: './upload-image.component.scss',
})
export class UploadImageComponent {
  @Input() set userImg(value: boolean) {
    this.userImgSignal.set(value);
  }

  @Input() set path(value: string) {
    this.imgPathSignal.set(value);
  }

  @Input() set imageFit(value: string) {
    this.imageFitSignal.set(value);
  }

  @Input() set customWidth(value: string) {
    this.customWidthSignal.set(value);
  }

  @Input() set customHeight(value: string) {
    this.customHeightSignal.set(value);
  }

  @Input() set disabled(value: boolean) {
    this.disabledSignal.set(value);
  }

  @Output() OnUpload = new EventEmitter();
  @Output() OnDelete = new EventEmitter();

  // Inject
  private readonly uploadService = inject(UploadImageService);
  private readonly loggingService = inject(LoggingService);
  private readonly translocoService = inject(TranslocoService);
  private readonly notificationBarService = inject(NotificationBarService);

  userImgSignal = signal<boolean>(true);
  imgPathSignal = signal<string>('');
  loadingSignal = signal<boolean>(false);
  imageFitSignal = signal<string>('object-contain');
  customWidthSignal = signal<string>('8rem');
  customHeightSignal = signal<string>('8rem');
  disabledSignal = signal<boolean>(false);

  contentDescDialog = signal<string>('');
  dialogSignal = signal<IDialogData>({
    image: '',
    title: '',
    description: '',
    delay: 2000,
    visible: false,
    useContent: false,
  });

  //NOTE - Notification Bar - Check Button Locked
  readonly isButtonLocked = this.notificationBarService.isButtonLocked;

  // Generate unique ID for file input to avoid conflicts with other upload components
  readonly fileInputId = `imgUpload-single-${Math.random().toString(36).substr(2, 9)}`;

  s3BlobPath = environment.s3.pubBlob;

  onSelectFile() {
    document.getElementById(this.fileInputId)?.click();
  }

  onFileSelected(event: Event) {
    const target = event.target as HTMLInputElement;
    if (target && target.files) {
      this.loggingService.warn('Files Selected ===>', target.files);
      this.uploadFile(target.files[0]);
      // Reset file input to allow selecting the same file again
      target.value = '';
    }
  }

  onDragOver(event: Event) {
    event.preventDefault();
  }

  onDrop(event: Event) {
    if (event.type === 'drop') {
      event.preventDefault();
      const dataTransfer = (event as DragEvent).dataTransfer;
      if (dataTransfer && dataTransfer.files.length > 0) {
        this.loggingService.warn('Files Drop ===>', dataTransfer.files[0]);
        this.uploadFile(dataTransfer.files[0]);
      }
    }
  }

  uploadFile(file: File) {
    // Check if file exists
    if (!file) {
      this.loggingService.error('No file provided for upload');
      return;
    }

    // Check file size (10MB = 10 * 1024 * 1024 bytes)
    const maxSizeInBytes = 10 * 1024 * 1024; // 10MB

    if (file.size > maxSizeInBytes) {
      this.loggingService.error('File size exceeds 10MB limit.', file.size);
      this.onOpenDialogAlert();
      return;
    }

    this.loadingSignal.set(true);

    this.uploadService.postImg(file).subscribe((pathRes: any) => {
      this.loggingService.warn('res Img', pathRes);
      this.imgPathSignal.set(pathRes.url);
      this.loadingSignal.set(false);
      this.OnUpload.emit(pathRes.url);
    });

    // FIXME For Test
    // setTimeout(() => {
    //   const pathUpload =
    //     'https://images.pexels.com/photos/11284324/pexels-photo-11284324.jpeg';
    //   this.imgPathSignal.set(pathUpload);
    //   this.loadingSignal.set(false);
    //   this.onUpload.emit(pathUpload);
    // }, 2000);
  }

  conditionsPath() {
    let pathReturn = `${this.s3BlobPath}${this.imgPathSignal()}`;
    const pathImg = this.imgPathSignal();
    if (pathImg.startsWith('https://')) {
      pathReturn = this.imgPathSignal();
    }
    return pathReturn;
  }

  onOpenDialogAlert() {
    const dataDialog: IDialogData = {
      image: PATH_ICON_ALERT_DIALOG.FAIL,
      title: this.translateWord('file_size_warning.title'),
      description: this.translateWord('try_again'),
      delay: 0,
      visible: true,
      useContent: true,
      isClosable: true,
    };
    const content = `<div class="text-xs text-bluegray-500 font-normal px-4">
                            <p>- ${this.translateWord('file_size_warning.message')}</p>
                          </div>`;

    // set dialog data
    this.dialogSignal.set(dataDialog);
    this.contentDescDialog.set(content);
  }

  onCloseDialogAlert() {
    this.dialogSignal.update((res) => ({
      ...res,
      visible: false,
    }));
    // NOTE: Case Upload Success
    if (this.dialogSignal().image.includes('success')) {
      this.contentDescDialog.set('');
    }
  }

  onDeleteImg() {
    this.imgPathSignal.set('');
    this.OnDelete.emit('');
  }

  translateWord(key: string) {
    return this.translocoService.translate(key);
  }
}
